<template>
  <div class="project-view">
    <!-- Project Header -->
    <div class="project-header">
      <div class="project-header-content">
        <button class="back-button" @click="$emit('close-project')" title="Back to chats">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Back
        </button>
        <div class="project-title-section">
          <h1 class="project-name">{{ project.name }}</h1>
          <p v-if="project.description" class="project-description">{{ project.description }}</p>
        </div>
        <div class="project-actions">
          <button class="action-btn" @click="$emit('new-chat-in-project', project)" title="New chat in this project">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New Chat
          </button>
          <button class="action-btn action-btn-secondary" @click="uploadDocument" title="Upload document">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Upload
          </button>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="project-tabs">
      <button
        class="tab"
        :class="{ active: activeTab === 'conversations' }"
        @click="activeTab = 'conversations'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        Conversations
        <span class="tab-count">{{ conversations.length }}</span>
      </button>
      <button
        class="tab"
        :class="{ active: activeTab === 'documents' }"
        @click="activeTab = 'documents'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        Documents
        <span class="tab-count">{{ documents.length }}</span>
      </button>
      <button
        class="tab"
        :class="{ active: activeTab === 'artifacts' }"
        @click="activeTab = 'artifacts'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
        Artifacts
        <span class="tab-count">{{ artifacts.length }}</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Conversations Tab -->
      <div v-if="activeTab === 'conversations'" class="tab-pane">
        <div v-if="conversations.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <p class="empty-title">No conversations yet</p>
          <p class="empty-subtitle">Start your first conversation in this project</p>
          <button class="empty-action-btn" @click="$emit('new-chat-in-project', project)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New Conversation
          </button>
        </div>
        <div v-else class="item-list">
          <div
            v-for="conv in conversations"
            :key="conv.id"
            class="list-item conversation-item"
            @click="$emit('open-conversation', conv)"
          >
            <div class="item-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-title">{{ conv.name || 'Untitled Chat' }}</div>
              <div class="item-meta">{{ formatDate(conv.created_at) }}</div>
            </div>
            <button class="item-action item-action-delete" @click.stop="deleteConversation(conv)" title="Delete conversation">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
            <div class="item-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents Tab -->
      <div v-if="activeTab === 'documents'" class="tab-pane">
        <div v-if="documents.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <p class="empty-title">No documents uploaded</p>
          <p class="empty-subtitle">Upload documents to provide context to your AI conversations</p>
          <button class="empty-action-btn" @click="uploadDocument">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Upload Document
          </button>
        </div>
        <div v-else class="item-list">
          <div
            v-for="doc in documents"
            :key="doc.id"
            class="list-item document-item"
          >
            <div class="item-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-title">
                {{ doc.file_name }}
                <span v-if="doc.parse_status === 'success'" class="parse-badge parse-success" title="Content extracted">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </span>
                <span v-else-if="doc.parse_status === 'partial'" class="parse-badge parse-partial" title="Partial extraction">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </span>
                <span v-else-if="doc.parse_status === 'failed'" class="parse-badge parse-failed" title="Extraction failed">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </span>
              </div>
              <div class="item-meta">Uploaded {{ formatDate(doc.uploaded_at) }}</div>
            </div>
            <button class="item-action" @click.stop="downloadDocument(doc)" title="Download">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
            <button class="item-action item-action-delete" @click.stop="deleteDocument(doc)" title="Delete">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Artifacts Tab -->
      <div v-if="activeTab === 'artifacts'" class="tab-pane">
        <div v-if="loadingArtifacts" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading artifacts...</p>
        </div>
        <div v-else-if="artifacts.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
              <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <p class="empty-title">No artifacts generated yet</p>
          <p class="empty-subtitle">Artifacts are files created by AI during conversations</p>
        </div>
        <div v-else class="item-list">
          <div
            v-for="artifact in artifacts"
            :key="artifact.id"
            class="list-item artifact-item"
          >
            <div class="item-icon">
              <svg v-if="artifact.file_type && artifact.file_type.startsWith('image/')" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-title">{{ artifact.file_name }}</div>
              <div class="item-meta">
                From {{ artifact.conversation_name || 'Conversation' }} &bull; {{ formatDate(artifact.created_at) }}
              </div>
            </div>
            <button class="item-action" @click.stop="downloadArtifact(artifact)" title="Download">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden file input for document upload -->
    <input
      ref="fileInput"
      type="file"
      style="display: none"
      @change="handleFileUpload"
    />
  </div>
</template>

<script>
import axios from '@/axios-config';

export default {
  name: 'ProjectView',
  props: {
    project: {
      type: Object,
      required: true
    },
    allConversations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      documents: [],
      artifacts: [],
      loadingArtifacts: false,
      activeTab: 'conversations'
    };
  },
  computed: {
    conversations() {
      if (!this.allConversations) return [];
      return this.allConversations.filter(conv => conv.project === this.project.id);
    }
  },
  mounted() {
    this.loadProjectData();
  },
  watch: {
    'project.id'() {
      this.loadProjectData();
    }
  },
  methods: {
    async loadProjectData() {
      try {
        // Load project details (includes documents)
        const projectResponse = await axios.get(`/api/projects/${this.project.id}/`);
        this.documents = projectResponse.data.documents || [];

        // Conversations are now passed as prop and filtered via computed property

        // Load artifacts
        this.loadArtifacts();
      } catch (error) {
        console.error('Error loading project data:', error);
      }
    },
    async loadArtifacts() {
      this.loadingArtifacts = true;
      try {
        const response = await axios.get(`/api/projects/${this.project.id}/artifacts/`);
        this.artifacts = response.data;
      } catch (error) {
        console.error('Error loading artifacts:', error);
        this.artifacts = [];
      } finally {
        this.loadingArtifacts = false;
      }
    },
    uploadDocument() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;

        await axios.post(`/api/projects/${this.project.id}/documents/`, formData, {
          headers: {
            'X-CSRFToken': token,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Reload project data
        await this.loadProjectData();

        // Reset input
        event.target.value = '';
      } catch (error) {
        console.error('Error uploading document:', error);
        alert('Failed to upload document');
      }
    },
    deleteConversation(conv) {
      if (!confirm(`Delete "${conv.name || 'Untitled Chat'}"?`)) return;
      this.$emit('delete-conversation', conv.id);
    },
    downloadDocument(doc) {
      window.open(doc.file_path, '_blank');
    },
    async deleteDocument(doc) {
      if (!confirm(`Delete "${doc.file_name}"?`)) return;
      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;
        await axios.delete(`/api/projects/${this.project.id}/documents/${doc.id}/`, {
          headers: { 'X-CSRFToken': token }
        });
        this.documents = this.documents.filter(d => d.id !== doc.id);
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    },
    downloadArtifact(artifact) {
      window.open(artifact.file_url, '_blank');
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString();
    },
    getArtifactIcon(fileType) {
      if (!fileType) return '📁';
      if (fileType.startsWith('image/')) return '🖼️';
      if (fileType.includes('pdf')) return '📄';
      if (fileType.includes('text')) return '📝';
      if (fileType.includes('zip') || fileType.includes('archive')) return '📦';
      return '📁';
    }
  }
};
</script>

<style scoped>
.project-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.project-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-secondary, #e8e8e8);
}

.project-header-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid var(--border-primary, #ccc);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary, #333);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.back-button:hover {
  background: var(--bg-hover, #f5f5f5);
  border-color: var(--accent-primary, #2563eb);
  color: var(--accent-primary, #2563eb);
}

.project-title-section {
  flex: 1;
}

.project-name {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary, #1a1a1a);
}

.project-description {
  margin: 0.5rem 0 0 0;
  font-size: 1rem;
  color: var(--text-secondary, #666);
  line-height: 1.5;
}

.project-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--accent-primary, #2563eb);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.625rem 1.25rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-btn:hover {
  background: var(--accent-hover, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.action-btn-secondary {
  background: transparent;
  color: var(--text-primary, #333);
  border: 1px solid var(--border-primary, #ccc);
}

.action-btn-secondary:hover {
  background: transparent;
  border-color: var(--accent-primary, #2563eb);
  color: var(--accent-primary, #2563eb);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Tabs */
.project-tabs {
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid var(--border-secondary, #e8e8e8);
  margin-bottom: 1.5rem;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-secondary, #666);
  transition: all 0.2s ease;
  margin-bottom: -1px;
}

.tab svg {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.tab:hover {
  color: var(--text-primary, #333);
  background: var(--bg-hover, #f5f5f5);
}

.tab:hover svg {
  opacity: 1;
}

.tab.active {
  color: var(--accent-primary, #2563eb);
  border-bottom-color: var(--accent-primary, #2563eb);
  background: none;
}

.tab.active svg {
  opacity: 1;
}

.tab-count {
  background: var(--bg-tertiary, #e8e8e8);
  color: var(--text-tertiary, #888);
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.tab.active .tab-count {
  background: var(--accent-light, #dbeafe);
  color: var(--accent-primary, #2563eb);
}

/* Tab Content */
.tab-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.tab-pane {
  height: 100%;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  margin-bottom: 1rem;
  color: var(--text-tertiary, #999);
}

.empty-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #333);
}

.empty-subtitle {
  margin: 0 0 1.5rem 0;
  font-size: 0.95rem;
  color: var(--text-tertiary, #999);
  max-width: 400px;
}

.empty-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--accent-primary, #2563eb);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.625rem 1.5rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.empty-action-btn:hover {
  background: var(--accent-hover, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--text-tertiary, #999);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary, #e0e0e0);
  border-top-color: var(--accent-primary, #2563eb);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Item List */
.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--bg-secondary, #f9f9f9);
  border: 1px solid var(--border-secondary, #e8e8e8);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.list-item:hover {
  background: white;
  border-color: var(--accent-primary, #2563eb);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.conversation-item {
  cursor: pointer;
}

.item-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary, #fff);
  border-radius: 8px;
  border: 1px solid var(--border-secondary, #e8e8e8);
  flex-shrink: 0;
  color: var(--text-secondary, #666);
  transition: color 0.2s ease, border-color 0.2s ease;
}

.list-item:hover .item-icon {
  color: var(--accent-primary, #2563eb);
  border-color: var(--accent-primary, #2563eb);
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 500;
  color: var(--text-primary, #1a1a1a);
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-meta {
  font-size: 0.85rem;
  color: var(--text-tertiary, #999);
  margin-top: 0.25rem;
}

.item-arrow {
  display: flex;
  align-items: center;
  color: var(--text-tertiary, #ccc);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.conversation-item:hover .item-arrow {
  color: var(--accent-primary, #2563eb);
  transform: translateX(4px);
}

.item-action {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid var(--border-secondary, #e8e8e8);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: var(--text-tertiary, #999);
  transition: all 0.2s ease;
  flex-shrink: 0;
  opacity: 0;
}

.list-item:hover .item-action {
  opacity: 1;
}

.item-action:hover {
  background: var(--accent-primary, #2563eb);
  border-color: var(--accent-primary, #2563eb);
  color: white;
}

.item-action-delete:hover {
  background: #dc2626;
  border-color: #dc2626;
}

/* Parse status badges */
.parse-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-left: 0.4rem;
  vertical-align: middle;
  line-height: 1;
}

.parse-success {
  background: #dcfce7;
  color: #16a34a;
}

.parse-partial {
  background: #fef9c3;
  color: #ca8a04;
}

.parse-failed {
  background: #fee2e2;
  color: #dc2626;
}
</style>
